import { useState, type ReactNode } from 'react';
import { useQuery, type QueryKey } from '@tanstack/react-query';
import { Check, Globe, KeyRound, Network, Router, Shield } from 'lucide-react';
import {
  IPV6_ADDRESS_MODE_LABELS,
  IPV6_PREFIX_MODE_LABELS,
  describeWanIpv6,
  ipv6Refusals,
  offerVlansToOnu,
  type Ipv6AddressMode,
  type Ipv6PrefixMode,
  type OltVlanListResponse,
  type OnuVlan,
  type Page,
  type VlanAddressing,
  type VlanMode,
  type WanIpv6,
  type WanOwner,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { ipv6SwitchOffReason, observedIpv6Summary, visibleIpv6Choices, type Ipv6TargetRead } from '../../lib/wan-ipv6';
import {
  addressingReady,
  defaultAddressing,
  defaultFirewall,
  defaultIpv6Intent,
  defaultIpv6Modes,
  ipv6ChoiceMatrix,
  wanWizardSteps,
} from '../../lib/wan-wizard';
import { Button, ErrorNotice, Input, Spinner } from '../../components/ui';
import { WizardDialog } from '../../components/WizardDialog';
import { Ipv6RefusalFold } from './Ipv6RefusalFold';
import { useWanApply } from './useWanApply';
import { WAN_OWNER_LABELS } from '../../lib/wan-apply';

/**
 * Bridging or routing, in one place — as a wizard, not a trigger.
 *
 * It is a property of the subscriber's VLAN and can be changed on the VLANs
 * tab, but that is not where anybody looks for it: "is this customer bridged?"
 * is a question about the connection, asked while looking at the connection.
 *
 * The first version's mode buttons WERE the apply: one click rewrote a live
 * subscriber, on the one dialog in the app that skipped the review every
 * other chassis action gets. Now nothing fires until the operator has seen
 * their whole choice restated — which ONU, which VLAN, attach or change,
 * old mode to new — and pressed Apply on that summary. Apply then CLOSES the
 * dialog and hands the chassis write to the background task tray (like Resync),
 * so the operator is not pinned to a spinner while an ONU re-provisions; the
 * toast reports what the chassis said, and a failure stays until acknowledged.
 *
 * STEPPED like the management-mode dialog (operator, 2026-09-13, with
 * screenshots of the one-screen version: "the steps should be like the mngt
 * mode modal", "looking like moving between tabs"): one decision per screen
 * under the pill row — VLAN → Mode → Addressing → IP protocol → Firewall →
 * Review — with Back / Next and Apply on Review; Addressing and IP protocol
 * leave the row when Bridging is chosen, Firewall when the chassis is not a
 * ZTE. The step list and every default live in `lib/wan-wizard.ts`, pure and
 * tested. And NO "leave as it is" anywhere (same instruction): the Firewall
 * select's "Leave as it is" and the "Leave as the CPE has it" IPv6 tile are
 * gone; every choice is a real option and each step OPENS on the current
 * value — the row's VLAN, mode and addressing (a PPPoE username and a static
 * address pre-filled from the row; the password is never echoed, so it is
 * typed again), the IPv6 record else what the CPE reads else the firmware
 * default, the firewall the CPE reports else the one the platform recorded
 * for the ONU. So a routed apply always carries an explicit `ipv6` intent
 * and, on a ZTE, an explicit `firewalled`; the Review step lists exactly
 * what is sent, and says plainly when a firewall default is an assumption
 * because neither the CPE nor the platform reports the ONU's state.
 *
 * "Opens on the current value" is also what keeps a pass-through Apply
 * harmless. An unchanged addressing rides the row PATCH as NOTHING — no
 * `addressing`, no particulars — so a Huawei-chassis row recorded PPPoE
 * (the compat API records them on either vendor) is not re-recorded as DHCP
 * and its encrypted password is not wiped by an operator who opened the
 * dialog to set IPv6 (`addressingPatch`); and the firmware-default IPv6
 * order puts Auto first, the one mode no dialect refuses, so a unit whose
 * CPE has not linked yet (judged `tr069 / unknown`, refusing nothing) is
 * not handed DHCPv6 to fail with at first link.
 *
 * Routing asks the question routing actually poses: HOW does the ONU get
 * its address — DHCP, PPPoE (an account), or a static assignment. The fields
 * come from the chassis's own grammar, probed live on the C300 (2026-08-29):
 * PPPoE is `wan-ip N mode pppoe username … password …`, static is a
 * chassis-level IP profile carrying gateway and DNS plus
 * `wan-ip N mode static ip-profile … ip-address … mask …`. Both are written
 * through POST /olts/:id/onus/:onuId/omci-wan in the SAME apply as the VLAN
 * row, and the row records the particulars (the PPPoE password encrypted,
 * never echoed back). Switching a routed subscriber to bridged stands the
 * ONU-terminated WAN down (`mode: "none"`) so the subscriber's own router can
 * take over cleanly.
 *
 * A Huawei chassis has no `wan-ip` verb — its subscriber WAN is provisioned
 * through service-ports and TR-069 — so on a Huawei OLT this dialog records
 * the intent and says so, and PPPoE/static are offered through the CPE's own
 * WAN editor instead.
 *
 * The IPv6 half (2026-09-12) is a RECORD on the same VLAN row. Null on the
 * row is "not managed" — what every row recorded before that day carries,
 * and what ~130 live units that are dual-stack by firmware default depend
 * on — and the dialog no longer OFFERS it: the step opens on the record, else
 * on what the CPE reads (dual stack when it reads dual, IPv4 when it reads
 * IPv4), else on the firmware default (dual stack, the first modes the
 * target allows), so a routed apply always names a protocol. IPv4-only is an
 * explicit choice; dual stack names an address mode and a prefix mode, each
 * on its own full-width row (operator: "split ipv6 and ipv6 into 2 rows").
 * It never rides the `wan-ip` line: the omci-wan route sends it to the CPE
 * over TR-069 (alone, on a ZTE ONU; beside the IPv4 half on a Huawei ONT).
 * The page reads GET …/wan-ipv6 once and runs the two shared pure functions
 * itself — `ipv6Refusals` greys the radios the target refuses, with the
 * sentence the server would refuse with, and `ipv6WritesFor` gives the exact
 * parameter list the Review step lists — so what is shown is what is sent.
 * Where the target refuses EVERY intent (an OMCI-managed ONU, a Tenda, a
 * Nokia) the step is absent and Review quotes the sentence: with "leave as
 * the CPE has it" gone, two greyed tiles would have held Next and taken the
 * VLAN / mode / firewall writes away from those units.
 *
 * The VLANs offered are the CHASSIS'S — every VLAN the OLT carries that it
 * does not manage ONUs on. A management VLAN is never offered, from the
 * chassis's own classification (`usedForMgmt`); the attach endpoint refuses
 * it too — this filter is the courtesy, that guard is the wall.
 *
 * VLAN 1 is the one that needs saying twice. It is off for an organization
 * until somebody turns it on, so it does not appear as a NEW choice — but an
 * ONU already on it keeps it in this select, keeps this dialog, and keeps its
 * WAN editable. Most of gachie1's fleet is on VLAN 1; a dialog that hid it
 * from them would be an outage dressed as a policy.
 */
export function WanModeDialog({
  base,
  oltId,
  onuId,
  vendor,
  routerLinked,
  serial,
  onClose,
  href,
  invalidate,
  cpeFirewall,
}: {
  base: string;
  oltId: string;
  onuId: string;
  vendor: string | null;
  /** Whether a router is linked on the ACS — a bridge is then built on it (2026-10-14). */
  routerLinked?: boolean;
  serial: string;
  onClose: () => void;
  /** Where the background toast/tray row links — the ONU's own page. */
  href?: string;
  /** Extra query keys to refresh once the write lands (the ONU page's own). */
  invalidate?: QueryKey[];
  /**
   * The firewall as the CPE itself reports it over TR-069 (`device.firewall`
   * on the ONU page — `X_HW_FirewallLevel` / `Firewall.Enable`), null when
   * there is no linked CPE or the model reports none. It is where the
   * Firewall step opens before the platform's own column is consulted, the
   * same order the Security card and the as-deployed pill use; without it a
   * unit the ACS sweep linked (its column never written) had this dialog
   * calling "not readable" a state the page showed two cards up.
   */
  cpeFirewall?: boolean | null;
}) {
  /**
   * The apply itself — the row PATCH/POST, the omci-wan call and the bridged
   * firewall verb — is the sequence the WAN card's IPv6 rows run too
   * (`lib/wan-apply.ts`, handed to the tray by `useWanApply`); this dialog
   * only builds the choice. It is where the choice is captured before the
   * dialog is gone.
   */
  const wanApply = useWanApply({ base, oltId, onuId, serial, ...(href ? { href } : {}), ...(invalidate ? { invalidate } : {}) });
  /** The index into `steps` below. Nothing reaches the chassis before Apply on Review. */
  const [step, setStep] = useState(0);

  const vlansQuery = useQuery({
    queryKey: ['onu-config', base, 'vlans'],
    queryFn: () => api.get<Page<OnuVlan>>(`${base}/vlans`),
  });
  const oltVlansQuery = useQuery({
    queryKey: ['olt-vlans', oltId],
    queryFn: () => api.get<OltVlanListResponse>(`/olts/${encodeURIComponent(oltId)}/vlans`),
  });
  /**
   * The firewall the platform recorded for this ONU — the same OMCI-config
   * read the ONU page, the Security card and the as-deployed pill share (one
   * query key, so it costs nothing here; without `refresh=1` the route serves
   * the stored snapshot, never a CLI session). It is where the Firewall step
   * opens when the CPE reports nothing (`cpeFirewall`): with "leave as it
   * is" gone, the apply always carries the firewall on a ZTE, and the honest
   * default is the state the ONU is in.
   */
  const omciQuery = useQuery({
    queryKey: ['onu', onuId, 'omci-config'],
    queryFn: () =>
      api.get<{ firewalled?: boolean | null }>(
        `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/omci-config`,
      ),
  });

  const attached = (vlansQuery.data?.items ?? []).filter(
    (v) => v.purpose !== 'management' && v.purpose !== 'voip',
  );
  const attachedByVlan = new Map(attached.map((v) => [v.vlanId, v] as const));
  /**
   * The chassis's subscriber VLANs — the set the operator actually picks from.
   *
   * The ONU's own VLANs are handed to the rule, and that is what stops this
   * select going empty. It used to take every non-management VLAN, which
   * offered VLAN 1 to everybody; filtering VLAN 1 out without `alreadyOn`
   * would have gone to the other extreme and left a subscriber on VLAN 1
   * looking at a picker with no way to name the VLAN they are on — on a
   * chassis carrying only VLAN 1 and a management VLAN, with no options at
   * all. Both are the same mistake: a picker that cannot represent the value
   * it is editing.
   */
  const offer = offerVlansToOnu(oltVlansQuery.data?.items ?? [], {
    vlan1Enabled: oltVlansQuery.data?.vlan1Selectable === true,
    alreadyOn: attached.map((v) => v.vlanId),
  });
  const offered = [...offer.offered].sort((a, b) => a.vlanId - b.vlanId);

  /**
   * The choice being built. Every piece of state is `null` until the operator
   * touches it, and null means "the current value" — the row's, the CPE's,
   * the platform's — derived below, so each step OPENS on what is already
   * there and reopening the dialog never silently flips anything. The VLAN
   * defaults to the ONU's own subscriber VLAN where it has one — that is the
   * one the question is usually about.
   */
  const [chosenVlanId, setChosenVlanId] = useState<number | null>(null);
  const [chosenMode, setChosenMode] = useState<VlanMode | null>(null);
  const [chosenAddressing, setChosenAddressing] = useState<VlanAddressing | null>(null);
  const [pppoeUser, setPppoeUser] = useState<string | null>(null);
  const [pppoePass, setPppoePass] = useState('');
  const [sIp, setSIp] = useState<string | null>(null);
  const [sMask, setSMask] = useState<string | null>(null);
  const [sGateway, setSGateway] = useState<string | null>(null);
  const [sDns, setSDns] = useState('');
  /**
   * null = the current state — what the CPE reports, else what the platform
   * recorded, else the platform's assumption, said on Review. ZTE-only: both
   * the /omci-wan and /firewall routes refuse Huawei, so the step is absent
   * there and nothing is sent.
   */
  const [firewalled, setFirewalled] = useState<boolean | null>(null);
  /**
   * WHO manages a routed WAN (operator, 2026-10-07: "set the wan with the
   * option of omci (olt only) or tr069"). null = the record's, and with none
   * recorded the platform decides off the router as it always has.
   */
  const [chosenOwner, setChosenOwner] = useState<WanOwner | null>(null);

  const effectiveVlanId = chosenVlanId ?? attached[0]?.vlanId ?? offered[0]?.vlanId ?? null;
  const attachedRow = effectiveVlanId !== null ? attachedByVlan.get(effectiveVlanId) : undefined;
  const isZte = vendor === 'zte';
  const ADDRESSING_LABEL: Record<VlanAddressing, string> = { dhcp: 'DHCP', static: 'static', pppoe: 'PPPoE' };
  /** The mode this apply carries: the operator's, else the row's; null only for a VLAN not on this ONU yet. */
  const effectiveMode: VlanMode | null = chosenMode ?? attachedRow?.mode ?? null;
  const effectiveOwner: WanOwner | null = chosenOwner ?? attachedRow?.wanOwner ?? null;
  /** The row's own addressing where it is routed — a bridged row's `dhcp` is a placeholder, not a record. */
  const recordedAddressing: VlanAddressing | null = attachedRow?.mode === 'route' ? attachedRow.addressing : null;
  const effectiveAddressing: VlanAddressing = chosenAddressing ?? defaultAddressing({ recorded: recordedAddressing });
  // The particulars open on the row's own — a PPPoE account's name, a static
  // address — so an operator who came to flip the firewall does not retype
  // the WAN. The password is never echoed back, so it alone is typed again
  // (on a ZTE chassis, where the wan-ip line is re-rendered; on a Huawei the
  // row's stored one is kept when nothing is typed).
  const effectivePppoeUser = pppoeUser ?? attachedRow?.pppoeUsername ?? '';
  const effectiveSIp = sIp ?? attachedRow?.staticIp ?? '';
  const effectiveSMask = sMask ?? attachedRow?.staticMask ?? '255.255.255.0';
  const effectiveSGateway = sGateway ?? attachedRow?.staticGateway ?? '';
  /** Whether the operator typed any particular — what turns "kept as recorded" into a write. */
  const particularsTyped =
    pppoeUser !== null || pppoePass !== '' || sIp !== null || sMask !== null || sGateway !== null || sDns !== '';
  /** The addressing rides the PATCH as nothing when the row is re-applied as it is (`addressingPatch`). */
  const addressingUnchanged =
    recordedAddressing !== null && effectiveAddressing === recordedAddressing && !particularsTyped;

  /**
   * The IPv6 half of the WAN — a RECORD with two operator-facing states now:
   *
   *   ipv4   IPv4 only — an EXPLICIT choice that switches IPv6 off on the CPE.
   *   dual   dual stack, with an address mode and a prefix mode.
   *
   * The row's null ("not managed", what every row before 2026-09-12 carries)
   * is no longer offered as a tile (operator: no "leave as it is"); the step
   * opens on the record, else the CPE's reading, else the firmware default —
   * `defaultIpv6Intent`, never on a refused mode. `null` in the state means
   * "not decided yet by the operator": the default decides.
   */
  const [ipv6Choice, setIpv6Choice] = useState<'ipv4' | 'dual' | null>(null);
  const [ipv6Address, setIpv6Address] = useState<Ipv6AddressMode | null>(null);
  const [ipv6Prefix, setIpv6Prefix] = useState<Ipv6PrefixMode | null>(null);

  /**
   * Where the intent would land, and what the CPE reads today — asked of the
   * API once per mode so the sentence shown here BEFORE Apply is exactly the
   * save-time gate's, and the parameter list is exactly the writer's. The
   * two judgements are the shared pure functions; only the target is fetched.
   */
  const ipv6TargetQuery = useQuery({
    queryKey: ['onu-config', base, 'wan-ipv6', effectiveMode ?? 'route'],
    queryFn: () =>
      api.get<Ipv6TargetRead>(`${base}/wan-ipv6?mode=${encodeURIComponent(effectiveMode ?? 'route')}`),
    enabled: effectiveVlanId !== null,
  });
  const ipv6Target = ipv6TargetQuery.data ?? null;
  const observed = ipv6Target?.observed ?? null;
  const recordedIpv6 = attachedRow?.ipv6 ?? null;
  /** The matrix read per radio for THIS target — what greys each one, and what a default must avoid. */
  const ipv6Matrix = ipv6Target ? ipv6ChoiceMatrix((intent) => ipv6Refusals(intent, ipv6Target.target)) : null;
  /**
   * Hidden, not greyed (operator, 2026-10-21: "hide options not supported …
   * until we add them"): the dual-stack tile goes when this unit has no
   * writable pair yet (`ipv6SwitchOffReason` says why, under the tiles), and
   * a mode refused with every pairing goes from its row — the WAN card's own
   * rule (`visibleIpv6Choices`); the current record stays visible, greyed.
   */
  const dualBlocked = ipv6Matrix ? ipv6SwitchOffReason(ipv6Matrix) : null;
  /**
   * Whether the target takes an IPv6 choice at all. False once it is read and
   * refuses even IPv4 only (an OMCI-managed ONU, a Tenda, a Nokia): the step
   * then leaves the row and Review quotes the sentence — the VLAN, mode and
   * firewall writes stay reachable on those units. While unread, or when the
   * read failed, the step stays and says so.
   */
  const ipv6Applicable = !(ipv6Matrix && ipv6Matrix.wholesale.length > 0);
  const ipv6ReadFailed = ipv6TargetQuery.isError && ipv6Target === null;
  const ipv6Default =
    ipv6Target && ipv6Applicable
      ? defaultIpv6Intent({
          record: recordedIpv6,
          observed,
          refusals: (intent) => ipv6Refusals(intent, ipv6Target.target),
        })
      : null;
  const ipv6DefaultModes =
    ipv6Target && ipv6Applicable && ipv6Matrix
      ? defaultIpv6Modes({
          record: recordedIpv6,
          observed,
          refusals: (intent) => ipv6Refusals(intent, ipv6Target.target),
          matrix: ipv6Matrix,
        })
      : null;
  const effectiveIpv6Choice: 'ipv4' | 'dual' | null =
    ipv6Choice ?? (ipv6Default ? (ipv6Default.protocol === 'ipv4' ? 'ipv4' : 'dual') : null);
  const effectiveIpv6Address: Ipv6AddressMode = ipv6Address ?? ipv6DefaultModes?.addressMode ?? 'none';
  const effectiveIpv6Prefix: Ipv6PrefixMode = ipv6Prefix ?? ipv6DefaultModes?.prefixMode ?? 'none';
  /**
   * The intent this apply carries. Null ONLY where there is nothing to carry:
   * a bridged choice (the server clears the record), a target that refuses
   * every intent, a target read that failed, or one not yet read. A routed
   * apply on a target that takes a choice always names a protocol.
   */
  const ipv6Intent: WanIpv6 | null =
    effectiveMode === 'bridge' || !ipv6Applicable || effectiveIpv6Choice === null
      ? null
      : effectiveIpv6Choice === 'ipv4'
        ? { protocol: 'ipv4' }
        : { protocol: 'dual_stack', addressMode: effectiveIpv6Address, prefixMode: effectiveIpv6Prefix };
  const ipv6BothNone =
    ipv6Intent?.protocol === 'dual_stack' && ipv6Intent.addressMode === 'none' && ipv6Intent.prefixMode === 'none';
  const ipv6Shown = ipv6Matrix
    ? visibleIpv6Choices(
        ipv6Matrix,
        effectiveIpv6Address && effectiveIpv6Prefix ? { address: effectiveIpv6Address, prefix: effectiveIpv6Prefix } : null,
        recordedIpv6,
      )
    : { address: [], prefix: [] };
  /** The gate's sentences for this combination on THIS ONU — shown before Apply, and Next waits on them. */
  const ipv6RefusalsNow: string[] =
    ipv6Intent && ipv6Target && !ipv6BothNone ? ipv6Refusals(ipv6Intent, ipv6Target.target) : [];


  /**
   * The firewall this apply carries on a ZTE, and where its default came
   * from: the CPE's own reading, else the platform's column, else a guess
   * said on Review — the Security card's order (`device.firewall ??
   * omci.firewalled`), so this dialog never calls "not readable" a state
   * the page shows.
   */
  const recordedFirewall = omciQuery.data?.firewalled ?? null;
  const firewallDefault = defaultFirewall({ cpe: cpeFirewall, recorded: recordedFirewall, mode: effectiveMode });
  /** The state the ONU is in as far as anything reports it — what "current" marks and Review compares against. */
  const currentFirewall: boolean | null = firewallDefault.assumed ? null : firewallDefault.value;
  const effectiveFirewalled = firewalled ?? firewallDefault.value;
  /** A bridge has no firewall to set (2026-10-14): no step, nothing sent, nothing on Review. */
  const firewallApplies = isZte && effectiveMode === 'route';

  const IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
  const ipv4Ready =
    effectiveMode === 'bridge' ||
    (effectiveMode === 'route' &&
      (effectiveAddressing === 'dhcp' ||
        // A stored password stands in server-side (2026-09-19): one is typed only when none is stored.
        (effectiveAddressing === 'pppoe' &&
          effectivePppoeUser.trim() !== '' &&
          (pppoePass !== '' || Boolean(attachedRow?.hasPppoePassword))) ||
        (effectiveAddressing === 'static' &&
          IPV4.test(effectiveSIp) &&
          IPV4.test(effectiveSMask) &&
          IPV4.test(effectiveSGateway) &&
          (sDns === '' || IPV4.test(sDns)))));

  /**
   * The steps that exist for THIS choice — the routing questions leave when
   * Bridging is chosen, Firewall when the chassis is not a ZTE, IP protocol
   * when the target refuses every intent. The index clamps so a row that
   * shrank under the operator (Bridging chosen on Mode) lands on the next
   * step that still exists.
   */
  const steps = wanWizardSteps({ mode: effectiveMode, firewallApplicable: isZte, ipv6Applicable });
  const current = steps[Math.min(step, steps.length - 1)]!;

  /**
   * Hand the write to the background tray and close. The choice is built
   * here, whole, because the dialog is gone by the time the task settles —
   * the toast has to be able to name what was changed without the component
   * still being mounted. What rides each call is `wanApplyPlan`'s:
   *
   *   - the row PATCH carries the mode, the addressing only when it changed
   *     or a particular was typed (`addressingPatch` — re-sent unchanged, an
   *     untyped PPPoE password would wipe the stored one), and the IPv6
   *     record whenever this apply carries an intent (absent only where
   *     there is none to carry: a target that refuses every intent, or a
   *     read that failed — so such a row keeps what it recorded);
   *   - on a ZTE chassis the omci-wan call — routing writes the WAN with the
   *     firewall folded into the same transcript (always carried now: the
   *     Firewall step opens on the ONU's current state and has no "leave as
   *     it is"; a model without the firewall ME answers `unknown ME` on that
   *     line alone and the tray names it) and the IPv6 half beside it (the
   *     route sends it to the CPE over TR-069, never on the wan-ip line); a
   *     routed subscriber going bridged stands the WAN down, and the bridge
   *     gets its firewall on the standalone verb, whose refusal is a note
   *     beside the landed VLAN, never the apply's fate.
   *
   * A Huawei chassis has no wan-ip verb, so no omci-wan call is made there:
   * the intent is recorded on the row and delivered by the first-link push.
   */
  const startApply = () => {
    if (effectiveVlanId === null || effectiveMode === null) return;
    onClose();
    wanApply.start({
      ask: 'wan',
      zte: isZte,
      routerLinked: Boolean(routerLinked),
      vlanId: effectiveVlanId,
      row: attachedRow ?? null,
      mode: effectiveMode,
      addressing: effectiveAddressing,
      particularsTyped,
      pppoeUsername: effectivePppoeUser,
      pppoePassword: pppoePass,
      staticIp: effectiveSIp,
      staticMask: effectiveSMask,
      staticGateway: effectiveSGateway,
      staticDns: sDns,
      // IPv6 is a router leaf: an OLT-managed WAN carries none.
      ipv6: effectiveMode === 'route' && effectiveOwner === 'omci' ? null : ipv6Intent,
      firewalled: firewallApplies ? effectiveFirewalled : undefined,
      ...(effectiveMode === 'route' && effectiveOwner ? { owner: effectiveOwner } : {}),
    });
  };

  const loading = vlansQuery.isLoading || oltVlansQuery.isLoading;

  /**
   * Whether Next may leave the current step — the same `canAdvance` idea as
   * the management dialog, one rule per step. A refused IPv6 combination
   * never reaches Review: the sentence is on the page and Next waits until
   * the choice is one this ONU can carry — and until the target it is judged
   * against has been read at all (while the read is in flight the gate cannot
   * run here, and letting Next through would have the server answer 400 or
   * accept, contradicting whatever this page just said). A read that FAILED
   * (the ACS briefly away) is a different thing: the step says so, Next goes
   * on with no IPv6 half, and Review says it is not written — the VLAN, mode
   * and firewall writes do not wait on an ACS that is down. Addressing: the
   * fields validate on a ZTE (the wan-ip line is re-rendered, a PPPoE line
   * needs the password typed again), while on a Huawei an UNCHANGED row
   * passes as it is — the addressing is a record there, and the row's own
   * particulars are kept (`addressingReady`).
   */
  const canAdvance =
    current.key === 'vlan'
      ? effectiveVlanId !== null
      : current.key === 'mode'
        ? effectiveMode !== null
        : current.key === 'addressing'
          ? addressingReady({
              zte: isZte,
              addressing: effectiveAddressing,
              recorded: recordedAddressing,
              particularsTyped,
              fieldsReady: ipv4Ready,
            })
          : current.key === 'ipv6'
            ? !ipv6TargetQuery.isLoading &&
              (ipv6ReadFailed || (ipv6Intent !== null && ipv6RefusalsNow.length === 0 && !ipv6BothNone))
            : true;

  const addressingCards: Array<{
    value: VlanAddressing;
    icon: ReactNode;
    title: string;
    body: string;
  }> = [
    {
      value: 'dhcp',
      icon: <Globe className="size-4" />,
      title: 'DHCP',
      body: 'The ONU asks the network for its address. Nothing else to fill in.',
    },
    {
      value: 'pppoe',
      icon: <KeyRound className="size-4" />,
      title: 'PPPoE',
      body: 'The ONU dials the account itself — username and password go to the chassis.',
    },
    {
      value: 'static',
      icon: <Router className="size-4" />,
      title: 'Static',
      body: 'A fixed address, mask and gateway, assigned by you.',
    },
  ];

  /** What the CPE reads / where IPv6 would land, in one sentence for the step's head. */
  const ipv6Situation: ReactNode = (() => {
    if (observed && observedIpv6Summary(observed)) {
      return (
        <>
          The CPE currently reads <span className="font-medium text-ink">{observedIpv6Summary(observed)}</span>
          {observed.linkLocal ? (
            <>
              {' '}
              (link-local <span className="font-mono">{observed.linkLocal}</span>)
            </>
          ) : null}
          .
        </>
      );
    }
    if (ipv6TargetQuery.isLoading) return 'Reading what the CPE carries…';
    if (ipv6ReadFailed) {
      return (
        <>
          Could not read where IPv6 would land (
          {ipv6TargetQuery.error instanceof Error ? ipv6TargetQuery.error.message : 'the API did not answer'}) — an IPv6
          choice waits until it can be judged, so this apply writes no IPv6 half and the record stays as it is.
        </>
      );
    }
    if (ipv6Target?.target.kind === 'tr069' && ipv6Target.target.dialect === 'unknown')
      return 'The CPE has not reported its subscriber WAN to the ACS yet — a choice here is recorded and delivered when it links.';
    if (ipv6Target?.target.kind === 'tr069' && ipv6Target.target.dialect === 'none')
      return 'The CPE reports no IPv6 leaf on its subscriber WAN.';
    return 'What the CPE carries has not been read.';
  })();


  return (
    <WizardDialog
      title={
        <span className="flex items-center gap-2">
          <Router className="size-4 text-muted" />
          WAN mode — {serial}
        </span>
      }
      maxWidth="xl"
      // The stepped + Review pill row, exactly as the OMCI/TR-069 management-mode
      // wizard draws it: one decision per screen, Back / Next, Apply on Review.
      steps={steps}
      currentStep={Math.min(step, steps.length - 1)}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex gap-2">
            {step > 0 && <Button onClick={() => setStep((s) => Math.max(0, Math.min(s, steps.length - 1) - 1))}>Back</Button>}
            {current.key !== 'review' ? (
              <Button
                variant="primary"
                disabled={loading || effectiveVlanId === null || !canAdvance}
                onClick={() => setStep((s) => Math.min(s + 1, steps.length - 1))}
              >
                Next
              </Button>
            ) : (
              <Button variant="primary" icon={<Check className="size-4" />} onClick={startApply}>
                Apply
              </Button>
            )}
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center p-6">
          <Spinner />
        </div>
      ) : vlansQuery.isError ? (
        // Distinguish "could not read" from "nothing there". Conflating
        // them told an operator to attach a VLAN they had just attached.
        <ErrorNotice error={vlansQuery.error as Error} />
      ) : oltVlansQuery.isError ? (
        <ErrorNotice error={oltVlansQuery.error as Error} />
      ) : effectiveVlanId === null ? (
        <p className="text-sm text-muted">
          {offer.vlan1Withheld
            ? 'The only VLANs this chassis carries are its management VLAN and VLAN 1, and VLAN 1 is not offered to subscribers for this organization. Turn it on in the organization’s settings, or on this OLT’s VLANs tab, or add a subscriber VLAN to the chassis.'
            : 'This chassis records no subscriber VLANs yet — every VLAN it carries is marked for management. Sync the OLT, or classify its VLANs on the OLT page first.'}
        </p>
      ) : (
        <>
          {/* ------------------------------------------------ VLAN */}
          {current.key === 'vlan' && (
            <label className="block">
              <span className="label">Subscriber VLAN — every one this chassis carries</span>
              <select
                className="input"
                value={effectiveVlanId}
                onChange={(e) => {
                  // A different VLAN is a different row: every "current value"
                  // below is that row's, so the operator's choices on the
                  // steps ahead go back to following it.
                  setChosenVlanId(Number(e.target.value));
                  setChosenMode(null);
                  setChosenAddressing(null);
                  setPppoeUser(null);
                  setSIp(null);
                  setSMask(null);
                  setSGateway(null);
                  setIpv6Choice(null);
                  setIpv6Address(null);
                  setIpv6Prefix(null);
                  setFirewalled(null);
                }}
              >
                {offered.map((v) => {
                  const row = attachedByVlan.get(v.vlanId);
                  return (
                    <option key={v.id} value={v.vlanId}>
                      VLAN {v.vlanId}
                      {v.name ? ` — ${v.name}` : ''}
                      {row
                        ? ` (on this ONU, ${row.mode === 'bridge' ? 'bridged' : `routed · ${row.addressing.toUpperCase()}`})`
                        : ' (not on this ONU yet)'}
                    </option>
                  );
                })}
              </select>
              {/* Said rather than left as a gap: "VLAN 1 is missing from
                  this list" and "this OLT has no VLAN 1" look identical
                  from here, and only one of them has an answer. */}
              {offer.vlan1Withheld && (
                <span className="mt-1 block text-xs text-faint">
                  This chassis carries VLAN 1, but it is not offered to subscribers for this organization. ONUs
                  already on it are unaffected.
                </span>
              )}
            </label>
          )}

          {/* ------------------------------------------------ Mode */}
          {current.key === 'mode' && (
            <div className="grid gap-3 sm:grid-cols-2">
              {(
                [
                  {
                    mode: 'bridge' as const,
                    icon: <Network className="size-4" />,
                    title: 'Bridging',
                    body: "The VLAN is handed to whatever is plugged in, and the subscriber's own router does PPPoE or DHCP.",
                  },
                  {
                    mode: 'route' as const,
                    icon: <Router className="size-4" />,
                    title: 'Routing',
                    body: 'The ONU terminates the connection and serves the LAN behind it.',
                  },
                ]
              ).map((choice) => (
                <button
                  key={choice.mode}
                  type="button"
                  onClick={() => setChosenMode(choice.mode)}
                  className={`rounded-lg border p-4 text-left transition ${
                    effectiveMode === choice.mode ? 'border-primary bg-primary-soft' : 'border-line hover:border-primary/40'
                  }`}
                >
                  <span className="flex items-center gap-2 font-medium text-ink">
                    {choice.icon}
                    {choice.title}
                    {attachedRow?.mode === choice.mode && <span className="text-xs font-normal text-muted">current</span>}
                    {effectiveMode === choice.mode && <Check className="size-4 text-primary" />}
                  </span>
                  <span className="mt-1 block text-xs text-muted">{choice.body}</span>
                </button>
              ))}
              {effectiveMode === 'route' && (
                <div className="sm:col-span-2">
                  <span className="label">Managed by</span>
                  <div className="mt-1.5 flex flex-wrap gap-x-6 gap-y-2">
                    {(['omci', 'tr069'] as const).map((o) => (
                      <label key={o} className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                        <input
                          type="radio"
                          name="wizard-wan-owner"
                          checked={effectiveOwner === o}
                          onChange={() => setChosenOwner(o)}
                        />
                        {WAN_OWNER_LABELS[o]}
                        {attachedRow?.wanOwner === o && <span className="text-xs text-muted">recorded</span>}
                      </label>
                    ))}
                  </div>
                  <span className="mt-1 block text-xs text-faint">
                    {effectiveOwner === null
                      ? "Not chosen — the platform decides off the router's own connections, as before."
                      : effectiveOwner === 'omci'
                        ? "The OLT's line, and nothing over TR-069 for the WAN; LAN, WiFi and the rest stay on the router. No IPv6."
                        : "The router's own connection over TR-069; no OLT WAN line."}{' '}
                    Apply deletes the WAN and builds it again.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ------------------------------------------------ Addressing */}
          {current.key === 'addressing' && (
            <>
              <div className="grid gap-2 sm:grid-cols-3">
                {addressingCards.map((a) => {
                  const disabled = !isZte && a.value !== 'dhcp';
                  return (
                    <button
                      key={a.value}
                      type="button"
                      disabled={disabled}
                      title={
                        disabled
                          ? 'A Huawei chassis provisions PPPoE and static through the CPE (TR-069) — use the device page’s WAN editor.'
                          : undefined
                      }
                      onClick={() => setChosenAddressing(a.value)}
                      className={`rounded-lg border p-3 text-left transition ${
                        effectiveAddressing === a.value ? 'border-primary bg-primary-soft' : 'border-line hover:border-primary/40'
                      } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                    >
                      <span className="flex items-center gap-1.5 text-sm font-medium text-ink">
                        {a.icon}
                        {a.title}
                        {attachedRow?.mode === 'route' && attachedRow.addressing === a.value && (
                          <span className="text-xs font-normal text-muted">current</span>
                        )}
                        {effectiveAddressing === a.value && <Check className="size-4 text-primary" />}
                      </span>
                      <span className="mt-1 block text-xs text-muted">{a.body}</span>
                    </button>
                  );
                })}
              </div>

              {/* A Huawei-chassis row recorded PPPoE or static opens on its
                  own tile, disabled or not: the addressing is a RECORD there
                  (delivered to the CPE at its next link), and the first cut's
                  DHCP default re-recorded such subscribers on a pass-through
                  Apply. Kept as it is unless the operator says otherwise. */}
              {!isZte && recordedAddressing !== null && recordedAddressing !== 'dhcp' && (
                <p className="text-xs text-muted">
                  The row records <span className="font-medium text-ink">{recordedAddressing.toUpperCase()}</span>
                  {addressingUnchanged
                    ? ' — kept as it is, particulars included, unless you choose DHCP or type new ones. A Huawei chassis provisions PPPoE and static through the CPE, so the record is what its next link delivers.'
                    : '.'}
                </p>
              )}

              {effectiveAddressing === 'pppoe' && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="label">PPPoE username</span>
                    <Input
                      value={effectivePppoeUser}
                      onChange={(e) => setPppoeUser(e.target.value)}
                      placeholder="subscriber@isp"
                      autoComplete="off"
                    />
                  </label>
                  <label className="block">
                    <span className="label">
                      PPPoE password
                      {attachedRow?.hasPppoePassword ? ' — one is stored, never shown; leave this blank to keep it' : ''}
                    </span>
                    <Input
                      type="password"
                      value={pppoePass}
                      onChange={(e) => setPppoePass(e.target.value)}
                      autoComplete="new-password"
                    />
                  </label>
                  <p className="text-xs text-muted sm:col-span-2">
                    The credentials go to the chassis and are stored here encrypted — they are never shown back.
                  </p>
                </div>
              )}

              {effectiveAddressing === 'static' && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="label">IP address</span>
                    <Input value={effectiveSIp} onChange={(e) => setSIp(e.target.value)} placeholder="41.90.x.x" />
                  </label>
                  <label className="block">
                    <span className="label">Subnet mask</span>
                    <Input value={effectiveSMask} onChange={(e) => setSMask(e.target.value)} placeholder="255.255.255.0" />
                  </label>
                  <label className="block">
                    <span className="label">Gateway</span>
                    <Input value={effectiveSGateway} onChange={(e) => setSGateway(e.target.value)} placeholder="41.90.x.1" />
                  </label>
                  <label className="block">
                    <span className="label">Primary DNS — optional</span>
                    <Input value={sDns} onChange={(e) => setSDns(e.target.value)} placeholder="8.8.8.8" />
                  </label>
                  <p className="text-xs text-muted sm:col-span-2">
                    Gateway and DNS become a shared chassis IP profile; the address and mask ride the ONU&rsquo;s own
                    WAN line.
                  </p>
                </div>
              )}
            </>
          )}

          {/* ------------------------------------------------ IP protocol */}
          {current.key === 'ipv6' && (
            <div className="space-y-3">
              <p className="text-xs text-muted">
                {ipv6Situation}
                {recordedIpv6 ? (
                  <>
                    {' '}
                    Recorded here: <span className="font-medium text-ink">{describeWanIpv6(recordedIpv6)}</span>.
                  </>
                ) : (
                  <> Nothing is recorded here yet.</>
                )}
              </p>

              {ipv6ReadFailed ? (
                <div className="flex items-center justify-between gap-3 rounded-md border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-warn">
                  <span>No IPv6 choice can be judged until the read succeeds.</span>
                  <Button onClick={() => void ipv6TargetQuery.refetch()}>Retry</Button>
                </div>
              ) : ipv6TargetQuery.isLoading || !ipv6Matrix ? (
                <div className="flex justify-center p-4">
                  <Spinner />
                </div>
              ) : (
                <>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {(
                      [
                        { value: 'ipv4' as const, title: 'IPv4 only', body: 'Switches IPv6 off on the CPE.' },
                        {
                          value: 'dual' as const,
                          title: 'Dual stack IPv4 + IPv6',
                          body: 'IPv6 beside IPv4, with the address and prefix modes below.',
                        },
                      ]
                    )
                      .filter((c) => c.value === 'ipv4' || dualBlocked === null || recordedIpv6?.protocol === 'dual_stack')
                      .map((c) => {
                      const protocol = c.value === 'ipv4' ? 'ipv4' : 'dual_stack';
                      return (
                        <label
                          key={c.value}
                          className={`flex cursor-pointer gap-2 rounded-lg border p-3 text-left transition ${
                            effectiveIpv6Choice === c.value ? 'border-primary bg-primary-soft' : 'border-line hover:border-primary/40'
                          }`}
                        >
                          <input
                            type="radio"
                            name="ipv6-protocol"
                            className="mt-1"
                            checked={effectiveIpv6Choice === c.value}
                            onChange={() => setIpv6Choice(c.value)}
                          />
                          <span>
                            <span className="block text-sm font-medium text-ink">
                              {c.title}
                              {recordedIpv6?.protocol === protocol && (
                                <span className="ml-1 text-xs font-normal text-muted">current record</span>
                              )}
                              {recordedIpv6 === null && observed?.protocol === protocol && (
                                <span className="ml-1 text-xs font-normal text-muted">as the CPE reads it</span>
                              )}
                            </span>
                            <span className="mt-0.5 block text-xs text-muted">{c.body}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {dualBlocked !== null && recordedIpv6?.protocol !== 'dual_stack' && (
                    <p className="text-xs text-muted">Dual stack is not offered on this CPE yet: {dualBlocked}</p>
                  )}

                  {effectiveIpv6Choice === 'dual' && (
                    // Two FULL-WIDTH rows, one per question (operator: "split
                    // ipv6 and ipv6 into 2 rows") — side by side they wrapped
                    // into each other on the dialog's width.
                    <div className="space-y-3">
                      <div>
                        <span className="label">IPv6 address</span>
                        <div className="mt-1 flex flex-wrap gap-4">
                          {ipv6Shown.address.map((m) => {
                            const refused = ipv6Matrix.address[m];
                            return (
                              <label
                                key={m}
                                title={refused[0]}
                                className={`flex items-center gap-1.5 text-sm ${
                                  refused.length > 0 ? 'cursor-not-allowed text-faint' : 'text-ink'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="ipv6-address"
                                  disabled={refused.length > 0}
                                  checked={effectiveIpv6Address === m}
                                  onChange={() => setIpv6Address(m)}
                                />
                                {IPV6_ADDRESS_MODE_LABELS[m]}
                                {recordedIpv6?.protocol === 'dual_stack' && recordedIpv6.addressMode === m && (
                                  <span className="text-xs text-muted">current record</span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <span className="label">IPv6 prefix delegation</span>
                        <div className="mt-1 flex flex-wrap gap-4">
                          {ipv6Shown.prefix.map((m) => {
                            const refused = ipv6Matrix.prefix[m];
                            return (
                              <label
                                key={m}
                                title={refused[0]}
                                className={`flex items-center gap-1.5 text-sm ${
                                  refused.length > 0 ? 'cursor-not-allowed text-faint' : 'text-ink'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="ipv6-prefix"
                                  disabled={refused.length > 0}
                                  checked={effectiveIpv6Prefix === m}
                                  onChange={() => setIpv6Prefix(m)}
                                />
                                {IPV6_PREFIX_MODE_LABELS[m]}
                                {recordedIpv6?.protocol === 'dual_stack' && recordedIpv6.prefixMode === m && (
                                  <span className="text-xs text-muted">current record</span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      {/* The gate's own sentences for the greyed radios, behind
                          a fold — the same component the WAN card draws. */}
                      <Ipv6RefusalFold matrix={ipv6Matrix} />
                    </div>
                  )}

                  {ipv6BothNone && (
                    <p className="text-xs text-danger">
                      Dual stack with neither an IPv6 address mode nor a prefix mode is IPv4 only — choose one, or
                      choose IPv4 only.
                    </p>
                  )}
                  {/* The gate's own sentence for the CURRENT combination, BEFORE
                      Apply: the same words the server would refuse with. Next
                      waits on it. Ordinarily empty — a default never opens on
                      a refused mode and a refused radio cannot be pressed. */}
                  {ipv6RefusalsNow.map((why) => (
                    <p key={why} className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">
                      {why}
                    </p>
                  ))}
                </>
              )}
            </div>
          )}

          {/* ------------------------------------------------ Firewall */}
          {current.key === 'firewall' && (
            <div className="space-y-3">
              <p className="text-xs text-muted">
                Whether the ONU answers unsolicited traffic on its WAN side.{' '}
                {firewallDefault.source === 'cpe'
                  ? `The CPE reports it ${firewallDefault.value ? 'on' : 'off'} over TR-069.`
                  : omciQuery.isLoading
                    ? 'Reading the state the platform recorded…'
                    : firewallDefault.source === 'recorded'
                      ? `The platform records it ${firewallDefault.value ? 'on' : 'off'}.`
                      : 'Neither the CPE nor the platform reports a firewall state for this ONU, so the choice below is an assumption until you confirm it.'}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  {
                    on: true,
                    title: 'Firewalled',
                    detail: 'The ONU refuses unsolicited traffic — not answerable from the WAN. What a subscriber router should be.',
                  },
                  {
                    on: false,
                    title: 'Reachable',
                    detail: 'The ONU answers from the WAN side. Only for a device you intend to reach directly.',
                  },
                ].map((choice) => (
                  <button
                    key={String(choice.on)}
                    type="button"
                    onClick={() => setFirewalled(choice.on)}
                    className={`flex w-full items-start gap-2 rounded-lg border p-3 text-left transition ${
                      effectiveFirewalled === choice.on ? 'border-primary bg-primary-soft' : 'border-line hover:border-primary/40'
                    }`}
                  >
                    <Shield className="mt-0.5 size-4 shrink-0 text-muted" />
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 text-sm font-medium text-ink">
                        {choice.title}
                        {currentFirewall === choice.on && <span className="text-xs font-normal text-muted">current</span>}
                        {effectiveFirewalled === choice.on && <Check className="size-4 text-primary" />}
                      </span>
                      <span className="block text-xs text-muted">{choice.detail}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ------------------------------------------------ Review */}
          {/*
            The choices, and only the choices (operator, 2026-09-19: "remove all
            that extra olt jargon from the wan mode wizard … just go straight
            to apply"). Which writer takes the WAN, which chassis line is
            re-stated, which TR-069 leaves ride — none of that is the
            operator's decision, so none of it is on this step; the tray says
            what landed, in the same plain words.
          */}
          {current.key === 'review' && (
            <>
              <p className="text-sm font-medium text-ink">Apply to {serial}:</p>
              <ul className="space-y-1.5 rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-sm text-muted">
                <li>
                  VLAN <span className="font-mono text-ink">{effectiveVlanId}</span> —{' '}
                  <span className="font-medium text-ink">
                    {effectiveMode === 'bridge' ? 'bridged' : `routed, ${ADDRESSING_LABEL[effectiveAddressing]}`}
                  </span>
                  {attachedRow && (attachedRow.mode !== effectiveMode || (effectiveMode === 'route' && attachedRow.addressing !== effectiveAddressing)) ? (
                    <span className="text-faint">
                      {' '}
                      (was {attachedRow.mode === 'bridge' ? 'bridged' : `routed, ${ADDRESSING_LABEL[attachedRow.addressing]}`})
                    </span>
                  ) : null}
                </li>
                {effectiveMode === 'route' && effectiveAddressing === 'pppoe' && (
                  <li>
                    PPPoE account <span className="font-mono text-ink">{effectivePppoeUser.trim()}</span>
                    <span className="text-faint">{pppoePass ? ' — password as typed' : ' — stored password'}</span>
                  </li>
                )}
                {effectiveMode === 'route' && effectiveAddressing === 'static' && (
                  <li>
                    Static{' '}
                    <span className="font-mono text-ink">
                      {effectiveSIp}/{effectiveSMask}
                    </span>{' '}
                    via <span className="font-mono text-ink">{effectiveSGateway}</span>
                    {sDns ? (
                      <>
                        , DNS <span className="font-mono text-ink">{sDns}</span>
                      </>
                    ) : null}
                  </li>
                )}
                {effectiveMode === 'route' && ipv6Intent && (
                  <li>
                    IPv6 <span className="font-medium text-ink">{describeWanIpv6(ipv6Intent)}</span>
                  </li>
                )}
                {firewallApplies && (
                  <li>
                    Firewall <span className="font-medium text-ink">{effectiveFirewalled ? 'on' : 'off'}</span>
                  </li>
                )}
              </ul>
              <p className="text-xs text-muted">
                The subscriber drops for a moment while the ONU re-provisions. This dialog closes and the change runs in
                the background; the tray says when it has landed.
              </p>
            </>
          )}
        </>
      )}
    </WizardDialog>
  );
}
